(() => {
  const DISMISS_KEY = 'bas_app_prompt_dismissed_until';
  const initialized = new WeakSet();
  const impressions = new Set();

  function platform() {
    const ua = navigator.userAgent || '';
    const ipadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    if (/iPhone|iPad|iPod/i.test(ua) || ipadOs) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    if (/Mobi/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  function isDismissed() {
    try {
      return Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now();
    } catch (_error) {
      return false;
    }
  }

  function rememberDismissal(days) {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400000));
    } catch (_error) {
      // Storage can be unavailable in privacy modes. Dismissal still applies for this view.
    }
  }

  function context(element, destinationType) {
    return {
      page_type: element.dataset.basAppPageType || '',
      product_handle: element.dataset.basAppProductHandle || '',
      product_sku: element.dataset.basAppProductSku || '',
      part_number: element.dataset.basAppPartNumber || '',
      variant_id: element.dataset.basAppVariantId || '',
      collection_handle: element.dataset.basAppCollection || '',
      search_term: element.dataset.basAppSearch || '',
      device_platform: platform(),
      placement: element.dataset.basAppPlacement || '',
      campaign: element.dataset.basAppCampaign || '',
      destination_type: destinationType || ''
    };
  }

  function publish(eventName, element, destinationType) {
    if (window.Shopify && Shopify.designMode) return;
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;
    Shopify.analytics.publish(`bas_app:${eventName}`, context(element, destinationType));
  }

  function currentContextUrl(element) {
    try {
      const destination = new URL(element.dataset.basAppContextUrl || window.location.href, window.location.origin);
      const current = new URL(window.location.href);
      if (current.searchParams.has('variant')) {
        destination.searchParams.set('variant', current.searchParams.get('variant'));
      }
      if (element.dataset.basAppPageType === 'search' && current.search) {
        destination.search = current.search;
      }
      return destination.toString();
    } catch (_error) {
      return window.location.href;
    }
  }

  function destinationFor(element, detectedPlatform) {
    if (element.dataset.basAppDeepLinks === 'true') {
      return { url: currentContextUrl(element), type: 'deep_link' };
    }

    if (detectedPlatform === 'ios' && element.dataset.basAppIosUrl) {
      return { url: element.dataset.basAppIosUrl, type: 'app_store_ios' };
    }
    if (detectedPlatform === 'android' && element.dataset.basAppAndroidUrl) {
      return { url: element.dataset.basAppAndroidUrl, type: 'app_store_android' };
    }
    return { url: element.dataset.basAppPageUrl || '/pages/app', type: 'app_landing_page' };
  }

  function observeImpression(element) {
    const key = [
      window.location.pathname,
      element.dataset.basAppPlacement || '',
      element.dataset.basAppProductHandle || ''
    ].join('|');

    if (impressions.has(key)) return;

    const fire = () => {
      if (impressions.has(key)) return;
      impressions.add(key);
      publish('app_prompt_impression', element, 'visible');
    };

    if (!('IntersectionObserver' in window)) {
      fire();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
      observer.disconnect();
      fire();
    }, { threshold: 0.5 });
    observer.observe(element);
  }

  function show(element) {
    element.hidden = false;
    requestAnimationFrame(() => element.classList.add('is-visible'));
    observeImpression(element);
  }

  function initializePrompt(element) {
    if (initialized.has(element)) return;
    initialized.add(element);

    const detectedPlatform = platform();
    const designMode = element.dataset.basAppDesignMode === 'true' || Boolean(window.Shopify && Shopify.designMode);
    const mobileOnly = element.dataset.basAppMobileOnly !== 'false';

    if (!designMode && isDismissed()) return;
    if (mobileOnly && detectedPlatform === 'desktop') return;

    const action = element.querySelector('[data-bas-app-action]');
    const destination = destinationFor(element, detectedPlatform);
    if (action) {
      action.href = destination.url;
      action.textContent = element.dataset.basAppDeepLinks === 'true'
        ? (element.dataset.basAppOpenLabel || 'Open BAS App')
        : (element.dataset.basAppInstallLabel || 'Get the BAS App');
      action.addEventListener('click', () => {
        publish('app_prompt_click', element, destination.type);
        if (destination.type === 'app_store_ios') publish('app_store_ios_click', element, destination.type);
        if (destination.type === 'app_store_android') publish('app_store_android_click', element, destination.type);
        if (destination.type === 'deep_link') publish('app_deeplink_click', element, destination.type);
      });
    }

    const close = element.querySelector('[data-bas-app-dismiss]');
    if (close) {
      close.addEventListener('click', () => {
        const days = Math.max(1, Number(element.dataset.basAppDismissDays || 14));
        element.hidden = true;
        element.classList.remove('is-visible');
        if (!designMode) rememberDismissal(days);
        publish('app_prompt_dismiss', element, 'dismissed');
      });
    }

    const delay = Math.max(0, Math.min(10000, Number(element.dataset.basAppDelay || 0)));
    window.setTimeout(() => show(element), designMode ? 0 : delay);
  }

  function initializeLanding(root) {
    root.querySelectorAll('[data-bas-app-landing]').forEach((landing) => {
      if (initialized.has(landing)) return;
      initialized.add(landing);

      const detectedPlatform = platform();
      const deepLinks = landing.dataset.basAppDeepLinks === 'true';
      const primary = landing.querySelector('[data-bas-app-landing-primary]');
      if (primary) {
        let destination = { url: landing.dataset.basAppPageUrl || '#store-options', type: 'store_options' };
        if (deepLinks) {
          destination = { url: window.location.origin, type: 'deep_link' };
        } else if (detectedPlatform === 'ios') {
          destination = { url: landing.dataset.basAppIosUrl, type: 'app_store_ios' };
        } else if (detectedPlatform === 'android') {
          destination = { url: landing.dataset.basAppAndroidUrl, type: 'app_store_android' };
        }
        primary.href = destination.url;
        primary.textContent = deepLinks ? 'Open BAS App' : (detectedPlatform === 'desktop' ? 'Choose your app store' : 'Get the BAS App');
        primary.addEventListener('click', () => publish('app_prompt_click', landing, destination.type));
      }

      landing.querySelectorAll('[data-bas-app-store-link]').forEach((link) => {
        link.addEventListener('click', () => {
          const store = link.dataset.basAppStoreLink;
          publish(store === 'ios' ? 'app_store_ios_click' : 'app_store_android_click', landing, `app_store_${store}`);
        });
      });
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-bas-app-promo]').forEach(initializePrompt);
    initializeLanding(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
